import React, { useState, useEffect } from 'react';
import { 
    Modal, 
    Box, 
    Typography, 
    TextField, 
    Button, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    Checkbox, 
    FormControlLabel,
    IconButton,
    Alert,
    Grid,
    Divider,
    CircularProgress,
    Autocomplete,
    Chip,
    Paper
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import { toast } from 'react-toastify';
import { updateSymbol } from '../../api/symbolApi';

// Prevent scroll wheel from changing numeric input values
const preventScrollOnNumberInputs = () => {
    const handleWheel = (e) => {
        if (e.target.type === 'number' && e.target.closest('.MuiTextField-root')) {
            e.preventDefault();
        }
    };

    // Add event listener to prevent scroll on number inputs
    document.addEventListener('wheel', handleWheel, { passive: false });
    
    // Cleanup function
    return () => {
        document.removeEventListener('wheel', handleWheel);
    };
};

// Validation schema
const validationSchema = yup.object({
    name: yup
        .string()
        .required('Symbol name is required')
        .min(1, 'Name must be at least 1 character')
        .max(50, 'Name cannot exceed 50 characters'),
    description: yup
        .string()
        .max(200, 'Description cannot exceed 200 characters')
        .nullable(),
    lpSymbolName: yup
        .string()
        .max(50, 'LP Symbol name cannot exceed 50 characters')
        .nullable(),
    digits: yup
        .number()
        .min(0, 'Digits must be at least 0')
        .max(10, 'Digits cannot exceed 10')
        .nullable(),
    contractSize: yup
        .number()
        .min(0, 'Contract size must be at least 0')
        .nullable(),
    minLotSize: yup
        .number()
        .min(0, 'Minimum lot size must be at least 0')
        .nullable(),
    maxLotSize: yup
        .number()
        .min(0, 'Maximum lot size must be at least 0')
        .nullable(),
    lotStep: yup
        .number()
        .min(0, 'Lot step must be at least 0')
        .nullable(),
    tickSize: yup
        .number()
        .min(0, 'Tick size must be at least 0')
        .nullable(),
    baseCurrency: yup
        .string()
        .max(10, 'Base currency cannot exceed 10 characters')
        .nullable(),
    quoteCurrency: yup
        .string()
        .max(10, 'Quote currency cannot exceed 10 characters')
        .nullable(),
    fixSymbol: yup
        .string()
        .max(50, 'FIX symbol cannot exceed 50 characters')
        .nullable(),
    securityType: yup
        .string()
        .max(50, 'Security type cannot exceed 50 characters')
        .nullable(),
    isActive: yup.boolean()
});

const EditSymbolModal = ({ open, onClose, onSuccess, symbol }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [baseCurrencyOptions, setBaseCurrencyOptions] = useState([]);
    const [quoteCurrencyOptions, setQuoteCurrencyOptions] = useState([]);

    // Prevent scroll wheel from changing numeric input values
    useEffect(() => {
        const cleanup = preventScrollOnNumberInputs();
        return cleanup;
    }, []);

    // Initialize currency options
    useEffect(() => {
        if (open) {
            const defaultCurrencies = [
                'USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'NZD',
                'XAU', 'XAG', 'BTC', 'ETH', 'LTC', 'XRP', 'ADA', 'DOT'
            ];
            setBaseCurrencyOptions(defaultCurrencies);
            setQuoteCurrencyOptions(defaultCurrencies);
        }
    }, [open]);

    // Reset form when modal opens/closes
    useEffect(() => {
        if (open && symbol) {
            // Formik will automatically reinitialize due to enableReinitialize: true
            console.log('EditSymbolModal: Symbol data loaded:', symbol);
        }
    }, [open, symbol]);

    // Initialize form with symbol data
    const formik = useFormik({
        initialValues: {
            name: symbol?.name || '',
            description: symbol?.description || '',
            lpId: symbol?.lpId || 0,
            lpSymbolName: symbol?.lpSymbolName || '',
            digits: symbol?.digits || 5,
            contractSize: symbol?.contractSize || 100000,
            minLotSize: symbol?.minLotSize || 0.01,
            maxLotSize: symbol?.maxLotSize || 100,
            lotStep: symbol?.lotStep || 0.01,
            tickSize: symbol?.tickSize || 0.00001,
            baseCurrency: symbol?.baseCurrency || '',
            quoteCurrency: symbol?.quoteCurrency || '',
            isActive: symbol?.isActive || true,
            fixSymbol: symbol?.fixSymbol || '',
            securityType: symbol?.securityType || 'Forex'
        },
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            setLoading(true);
            setError(null);

            try {
                // Get token from localStorage
                const token = localStorage.getItem('token');
                if (!token) {
                    throw new Error('Authentication token not found');
                }

                // Prepare data for API call - match the exact structure from API response
                const symbolData = {
                    name: values.name,
                    description: values.description || "",
                    lpId: parseInt(values.lpId) || 0,
                    lpSymbolName: values.lpSymbolName || "",
                    digits: parseInt(values.digits) || 5,
                    contractSize: parseFloat(values.contractSize) || 100000,
                    minLotSize: parseFloat(values.minLotSize) || 0.01,
                    maxLotSize: parseFloat(values.maxLotSize) || 100,
                    lotStep: parseFloat(values.lotStep) || 0.01,
                    tickSize: parseFloat(values.tickSize) || 0.00001,
                    baseCurrency: values.baseCurrency || "",
                    quoteCurrency: values.quoteCurrency || "",
                    isActive: Boolean(values.isActive),
                    fixSymbol: values.fixSymbol || "",
                    securityType: values.securityType || "Forex"
                };

                console.log('Updating Symbol Data:', symbolData);
                console.log('Symbol ID being used:', symbol.id);
                console.log('Symbol object:', symbol);

                // Validate that we have a symbol ID
                if (!symbol.id) {
                    throw new Error('Symbol ID is missing. Cannot update symbol without ID.');
                }

                // Call API
                const result = await updateSymbol(token, symbol.id, symbolData);
                
                // Success - handle both 200 and 204 responses
                if (result.success || result.message) {
                    toast.success(`Symbol "${values.name}" updated successfully!`);
                } else {
                    toast.success(`Symbol "${values.name}" updated successfully!`);
                }
                
                // Dispatch custom event for real-time updates
                window.dispatchEvent(new CustomEvent('symbolUpdated', { 
                    detail: { 
                        symbol: { ...values, id: symbol.id },
                        action: 'update'
                    } 
                }));
                
                onSuccess && onSuccess(result);
                onClose();
                
            } catch (error) {
                console.error('Error updating symbol:', error);
                
                // Handle specific error messages from the API
                let errorMessage = 'Failed to update symbol';
                
                if (error.message) {
                    errorMessage = error.message;
                } else if (error.error) {
                    errorMessage = error.error;
                } else if (typeof error === 'string') {
                    errorMessage = error;
                }
                
                // Check for specific error types
                if (errorMessage.includes('already exists')) {
                    errorMessage = `❌ ${errorMessage}\n\n💡 Please choose a different symbol name or LP symbol name.`;
                } else if (errorMessage.includes('validation')) {
                    errorMessage = `❌ Validation Error: ${errorMessage}\n\n💡 Please check all required fields and try again.`;
                } else if (errorMessage.includes('missing')) {
                    errorMessage = `❌ ${errorMessage}\n\n💡 Please refresh the page and try again.`;
                }
                
                setError(errorMessage);
                toast.error(errorMessage);
            } finally {
                setLoading(false);
            }
        }
    });

    // Reset form when modal opens/closes
    useEffect(() => {
        if (open && symbol) {
            console.log('EditSymbolModal - Received symbol data:', symbol);
            console.log('EditSymbolModal - Symbol ID:', symbol.id);
            console.log('EditSymbolModal - Available fields:', Object.keys(symbol));
            
            formik.resetForm({
                values: {
                    name: symbol.name || '',
                    description: symbol.description || '',
                    lpId: symbol.lpId || 0,
                    lpSymbolName: symbol.lpSymbolName || '',
                    digits: symbol.digits || 5,
                    contractSize: symbol.contractSize || 100000,
                    minLotSize: symbol.minLotSize || 0.01,
                    maxLotSize: symbol.maxLotSize || 100,
                    lotStep: symbol.lotStep || 0.01,
                    tickSize: symbol.tickSize || 0.00001,
                    baseCurrency: symbol.baseCurrency || '',
                    quoteCurrency: symbol.quoteCurrency || '',
                    isActive: symbol.isActive || true,
                    fixSymbol: symbol.fixSymbol || '',
                    securityType: symbol.securityType || 'Forex'
                }
            });
            setError(null);
        }
    }, [open, symbol]);

    const handleClose = () => {
        if (!loading) {
            formik.resetForm();
            setError(null);
            onClose();
        }
    };

    const securityTypes = [
        'Forex',
        'Metal',
        'Index',
        'Crypto',
        'Stock',
        'Commodity',
        'Bond',
        'Future',
        'Option'
    ];

    const currencies = [
        'USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'NZD',
        'XAU', 'XAG', 'BTC', 'ETH', 'LTC', 'XRP', 'ADA', 'DOT'
    ];

    return (
        <>
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="edit-symbol-modal-title"
            aria-describedby="edit-symbol-modal-description"
            sx={{
                zIndex: 1300
            }}
        >
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '95%', sm: '90%', md: '85%', lg: '80%' },
                maxWidth: 1000,
                maxHeight: '90vh',
                bgcolor: 'rgba(24, 31, 44, 0.95)',
                border: '2px solid rgba(6, 182, 212, 0.3)',
                borderRadius: 3,
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}>
                {/* Header */}
                <Box sx={{
                    p: 3,
                    borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
                    background: 'rgba(6, 182, 212, 0.1)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <EditIcon sx={{ color: '#00cfff', fontSize: 28 }} />
                        <Box>
                            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
                                Edit Symbol
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                Update symbol information and settings
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton 
                        onClick={handleClose} 
                        disabled={loading}
                        sx={{ color: '#fff' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Content */}
                <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                    {error && (
                        <Alert 
                            severity="error" 
                            sx={{ 
                                mb: 3,
                                background: 'rgba(211, 47, 47, 0.1)',
                                border: '1px solid #d32f2f',
                                color: '#fff',
                                '& .MuiAlert-icon': { color: '#ff6b6b' },
                                '& .MuiAlert-message': { 
                                    whiteSpace: 'pre-line',
                                    fontSize: '0.9rem'
                                }
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    <form onSubmit={formik.handleSubmit}>
                        <Grid container spacing={3}>
                            {/* Basic Information */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ color: '#1de9b6', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <InfoIcon sx={{ fontSize: 20 }} />
                                    Basic Information
                                </Typography>
                                <Divider sx={{ borderColor: 'rgba(6, 182, 212, 0.2)', mb: 2 }} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    id="name"
                                    name="name"
                                    label="Symbol Name *"
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.name && Boolean(formik.errors.name)}
                                    helperText={formik.touched.name && formik.errors.name}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    id="lpSymbolName"
                                    name="lpSymbolName"
                                    label="LP Symbol Name"
                                    value={formik.values.lpSymbolName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.lpSymbolName && Boolean(formik.errors.lpSymbolName)}
                                    helperText={formik.touched.lpSymbolName && formik.errors.lpSymbolName}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    id="description"
                                    name="description"
                                    label="Description"
                                    multiline
                                    rows={2}
                                    value={formik.values.description}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.description && Boolean(formik.errors.description)}
                                    helperText={formik.touched.description && formik.errors.description}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            {/* Trading Parameters */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ color: '#1de9b6', mb: 2, mt: 2 }}>
                                    Trading Parameters
                                </Typography>
                                <Divider sx={{ borderColor: 'rgba(6, 182, 212, 0.2)', mb: 2 }} />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    id="digits"
                                    name="digits"
                                    label="Decimal Digits"
                                    type="number"
                                    value={formik.values.digits}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.digits && Boolean(formik.errors.digits)}
                                    helperText={formik.touched.digits && formik.errors.digits}
                                    inputProps={{ min: 0, max: 10 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    id="contractSize"
                                    name="contractSize"
                                    label="Contract Size"
                                    type="number"
                                    value={formik.values.contractSize}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.contractSize && Boolean(formik.errors.contractSize)}
                                    helperText={formik.touched.contractSize && formik.errors.contractSize}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    id="tickSize"
                                    name="tickSize"
                                    label="Tick Size"
                                    type="number"
                                    value={formik.values.tickSize}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.tickSize && Boolean(formik.errors.tickSize)}
                                    helperText={formik.touched.tickSize && formik.errors.tickSize}
                                    inputProps={{ min: 0, step: 0.00001 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            {/* Lot Sizes */}
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    id="minLotSize"
                                    name="minLotSize"
                                    label="Min Lot Size"
                                    type="number"
                                    value={formik.values.minLotSize}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.minLotSize && Boolean(formik.errors.minLotSize)}
                                    helperText={formik.touched.minLotSize && formik.errors.minLotSize}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    id="maxLotSize"
                                    name="maxLotSize"
                                    label="Max Lot Size"
                                    type="number"
                                    value={formik.values.maxLotSize}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.maxLotSize && Boolean(formik.errors.maxLotSize)}
                                    helperText={formik.touched.maxLotSize && formik.errors.maxLotSize}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    id="lotStep"
                                    name="lotStep"
                                    label="Lot Step"
                                    type="number"
                                    value={formik.values.lotStep}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.lotStep && Boolean(formik.errors.lotStep)}
                                    helperText={formik.touched.lotStep && formik.errors.lotStep}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            {/* Currency Information */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ color: '#1de9b6', mb: 2, mt: 2 }}>
                                    Currency Information
                                </Typography>
                                <Divider sx={{ borderColor: 'rgba(6, 182, 212, 0.2)', mb: 2 }} />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Autocomplete
                                    id="baseCurrency"
                                    freeSolo
                                    options={baseCurrencyOptions}
                                    value={formik.values.baseCurrency}
                                    onChange={(event, newValue) => {
                                        formik.setFieldValue('baseCurrency', newValue || '');
                                    }}
                                    onInputChange={(event, newInputValue) => {
                                        formik.setFieldValue('baseCurrency', newInputValue);
                                    }}
                                    onBlur={formik.handleBlur}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Base Currency"
                                            error={formik.touched.baseCurrency && Boolean(formik.errors.baseCurrency)}
                                            helperText={formik.touched.baseCurrency && formik.errors.baseCurrency}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    color: '#fff',
                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                    '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                                },
                                                '& .MuiInputLabel-root': { color: '#bfc9db' }
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props}>
                                            <Chip 
                                                label={option} 
                                                size="small" 
                                                sx={{ 
                                                    backgroundColor: 'rgba(6, 182, 212, 0.1)',
                                                    color: '#1de9b6',
                                                    border: '1px solid rgba(6, 182, 212, 0.3)'
                                                }} 
                                            />
                                        </Box>
                                    )}
                                    PaperComponent={({ children, ...other }) => (
                                        <Paper 
                                            {...other} 
                                            sx={{ 
                                                backgroundColor: 'rgba(24, 31, 44, 0.95)',
                                                border: '1px solid rgba(6, 182, 212, 0.3)',
                                                '& .MuiAutocomplete-option': {
                                                    color: '#fff',
                                                    '&:hover': { backgroundColor: 'rgba(6, 182, 212, 0.1)' }
                                                }
                                            }}
                                        >
                                            {children}
                                        </Paper>
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Autocomplete
                                    id="quoteCurrency"
                                    freeSolo
                                    options={quoteCurrencyOptions}
                                    value={formik.values.quoteCurrency}
                                    onChange={(event, newValue) => {
                                        formik.setFieldValue('quoteCurrency', newValue || '');
                                    }}
                                    onInputChange={(event, newInputValue) => {
                                        formik.setFieldValue('quoteCurrency', newInputValue);
                                    }}
                                    onBlur={formik.handleBlur}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Quote Currency"
                                            error={formik.touched.quoteCurrency && Boolean(formik.errors.quoteCurrency)}
                                            helperText={formik.touched.quoteCurrency && formik.errors.quoteCurrency}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    color: '#fff',
                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                    '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                                },
                                                '& .MuiInputLabel-root': { color: '#bfc9db' }
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props}>
                                            <Chip 
                                                label={option} 
                                                size="small" 
                                                sx={{ 
                                                    backgroundColor: 'rgba(6, 182, 212, 0.1)',
                                                    color: '#1de9b6',
                                                    border: '1px solid rgba(6, 182, 212, 0.3)'
                                                }} 
                                            />
                                        </Box>
                                    )}
                                    PaperComponent={({ children, ...other }) => (
                                        <Paper 
                                            {...other} 
                                            sx={{ 
                                                backgroundColor: 'rgba(24, 31, 44, 0.95)',
                                                border: '1px solid rgba(6, 182, 212, 0.3)',
                                                '& .MuiAutocomplete-option': {
                                                    color: '#fff',
                                                    '&:hover': { backgroundColor: 'rgba(6, 182, 212, 0.1)' }
                                                }
                                            }}
                                        >
                                            {children}
                                        </Paper>
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Autocomplete
                                    id="securityType"
                                    freeSolo
                                    options={securityTypes}
                                    value={formik.values.securityType}
                                    onChange={(event, newValue) => {
                                        formik.setFieldValue('securityType', newValue || '');
                                    }}
                                    onInputChange={(event, newInputValue) => {
                                        formik.setFieldValue('securityType', newInputValue);
                                    }}
                                    onBlur={formik.handleBlur}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Security Type"
                                            error={formik.touched.securityType && Boolean(formik.errors.securityType)}
                                            helperText={formik.touched.securityType && formik.errors.securityType}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    color: '#fff',
                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                    '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                                },
                                                '& .MuiInputLabel-root': { color: '#bfc9db' }
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props}>
                                            <Chip 
                                                label={option} 
                                                size="small" 
                                                sx={{ 
                                                    backgroundColor: 'rgba(6, 182, 212, 0.1)',
                                                    color: '#1de9b6',
                                                    border: '1px solid rgba(6, 182, 212, 0.3)'
                                                }} 
                                            />
                                        </Box>
                                    )}
                                    PaperComponent={({ children, ...other }) => (
                                        <Paper 
                                            {...other} 
                                            sx={{ 
                                                backgroundColor: 'rgba(24, 31, 44, 0.95)',
                                                border: '1px solid rgba(6, 182, 212, 0.3)',
                                                '& .MuiAutocomplete-option': {
                                                    color: '#fff',
                                                    '&:hover': { backgroundColor: 'rgba(6, 182, 212, 0.1)' }
                                                }
                                            }}
                                        >
                                            {children}
                                        </Paper>
                                    )}
                                />
                            </Grid>

                            {/* Additional Information */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ color: '#1de9b6', mb: 2, mt: 2 }}>
                                    Additional Information
                                </Typography>
                                <Divider sx={{ borderColor: 'rgba(6, 182, 212, 0.2)', mb: 2 }} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    id="fixSymbol"
                                    name="fixSymbol"
                                    label="FIX Symbol"
                                    value={formik.values.fixSymbol}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.fixSymbol && Boolean(formik.errors.fixSymbol)}
                                    helperText={formik.touched.fixSymbol && formik.errors.fixSymbol}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            id="isActive"
                                            name="isActive"
                                            checked={formik.values.isActive}
                                            onChange={formik.handleChange}
                                            sx={{
                                                color: '#1de9b6',
                                                '&.Mui-checked': { color: '#1de9b6' }
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ color: '#fff' }}>
                                            Active Status
                                        </Typography>
                                    }
                                />
                            </Grid>
                        </Grid>
                    </form>
                </Box>

                {/* Footer */}
                <Box sx={{
                    p: 3,
                    borderTop: '1px solid rgba(6, 182, 212, 0.2)',
                    background: 'rgba(6, 182, 212, 0.1)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2
                }}>
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        disabled={loading}
                        sx={{
                            color: '#bfc9db',
                            borderColor: 'rgba(6, 182, 212, 0.3)',
                            textTransform: 'none',
                            '&:hover': {
                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                background: 'rgba(6, 182, 212, 0.1)'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={formik.handleSubmit}
                        disabled={loading || !formik.isValid}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        sx={{
                            background: '#00cfff',
                            color: '#000',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                background: '#00b8e6'
                            },
                            '&:disabled': {
                                background: 'rgba(0, 207, 255, 0.3)',
                                color: 'rgba(0, 0, 0, 0.3)'
                            }
                        }}
                    >
                        {loading ? 'Updating...' : 'Update Symbol'}
                    </Button>
                </Box>
            </Box>
        </Modal>
        </>
    );
};

export default EditSymbolModal;

